import React, { useEffect } from 'react';
import { X, MapPin, User, Eye, ListChecks, Lightbulb } from 'lucide-react';

const HowToPlay = ({ isOpen, onClose, t }) => {
    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.classList.contains('modal-backdrop')) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-spy border border-spy-border2 bg-spy-surface shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-spy-border p-4">
                    <h2 className="font-display text-[24px] font-medium text-spy-text">
                        {t.howToPlayTitle}
                    </h2>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="rounded-full p-2 text-spy-muted hover:text-spy-primary"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                    <p className="text-center text-sm text-spy-muted">
                        {t.howToPlaySubtitle}
                    </p>

                    {/* Game Modes */}
                    <div className="border-y border-spy-border py-3">
                        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-spy-primary">
                            Game Modes
                        </h3>
                        <div className="space-y-2 text-xs">
                            <div className="flex items-center space-x-2">
                                <MapPin className="h-3 w-3 text-spy-primary" />
                                <span className="text-spy-muted">
                                    <strong>Location:</strong> Guess the secret location (with roles)
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <User className="h-3 w-3 text-spy-primary" />
                                <span className="text-spy-muted">
                                    <strong>Celebrity:</strong> Guess the famous person
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Game Steps */}
                    <div className="space-y-2">
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-spy-text">
                            <ListChecks className="h-4 w-4 text-spy-primary" />
                            <span>How to Play</span>
                        </h3>
                        {t.gameplaySteps.map((step, index) => (
                            <div key={index} className="flex items-start space-x-2">
                                <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-spy-border2 font-display text-xs font-semibold text-spy-primary">
                                    {index + 1}
                                </div>
                                <p className="text-xs leading-relaxed text-spy-muted">
                                    {step}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Examples */}
                    <div className="border-y border-spy-border py-3">
                        <h3 className="mb-2 flex items-center text-sm font-semibold text-spy-text">
                            <Eye className="mr-1 h-3 w-3 text-spy-primary" />
                            Role Examples
                        </h3>

                        <div className="space-y-1 text-xs">
                            <div className="flex items-center space-x-2">
                                <div className="h-2 w-2 rounded-full bg-spy-good"></div>
                                <span className="text-spy-muted">
                                    <strong>Non-spy:</strong> "Restaurant" + "Chef"
                                </span>
                            </div>

                            <div className="flex items-center space-x-2">
                                <div className="h-2 w-2 rounded-full bg-spy-danger"></div>
                                <span className="text-spy-muted">
                                    <strong>Spy:</strong> "You are the spy!"
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tips */}
                    <div>
                        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-spy-text">
                            <Lightbulb className="h-4 w-4 text-spy-primary" />
                            <span>Strategy Tips</span>
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                            {t.tipsList.map((tip, index) => (
                                <div key={index} className="flex items-start text-xs text-spy-muted">
                                    <span className="mr-2 mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-spy-primary"></span>
                                    {tip}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="min-h-[48px] w-full rounded-spy bg-spy-text px-4 py-2.5 text-sm font-semibold text-spy-bg hover:opacity-90"
                    >
                        Got it!
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HowToPlay;
